/**
 * Physical constants and celestial body parameters.
 *
 * ## Units
 *
 * The entire package works in strict SI base units — **metres, seconds,
 * kilograms, radians, kelvin, pascals, newtons**. There are no kilometres and no
 * degrees below the presentation layer. Unit conversion happens exactly once, at
 * the UI boundary, via `units.ts`. Every mixed-unit bug in a trajectory tool
 * comes from relaxing this rule somewhere in the middle.
 *
 * Sources: WGS84 (NIMA TR8350.2), IERS Conventions 2010, EGM96 zonal harmonics,
 * JPL DE440 planetary GM values, US Standard Atmosphere 1976.
 */

/** Standard gravity, defined exactly. Used for ISP → exhaust velocity. */
export const G0 = 9.80665;

/** Newtonian constant of gravitation (CODATA 2018), m^3 kg^-1 s^-2. */
export const G_CONST = 6.6743e-11;

/** Astronomical unit, defined exactly (IAU 2012), metres. */
export const AU = 1.495978707e11;

/** Stefan–Boltzmann constant, W m^-2 K^-4. */
export const SIGMA_SB = 5.670374419e-8;

/** Solar irradiance at 1 AU, W/m^2 — drives solar radiation pressure. */
export const SOLAR_CONSTANT = 1361;

/** Speed of light in vacuum, m/s. */
export const C_LIGHT = 299792458;

/** Two pi, used often enough to be worth naming. */
export const TAU = 2 * Math.PI;

export interface CelestialBodyParams {
  readonly id: string;
  readonly name: string;
  /** Standard gravitational parameter GM, m^3/s^2. */
  readonly mu: number;
  /** Equatorial radius, m. */
  readonly equatorialRadius: number;
  /** Polar radius, m. */
  readonly polarRadius: number;
  /** Flattening (a - b) / a, dimensionless. */
  readonly flattening: number;
  /** Sidereal rotation rate, rad/s. */
  readonly rotationRate: number;
  /** Sidereal rotation period, s. */
  readonly rotationPeriod: number;
  /** Obliquity of the equator to its orbital plane, rad. */
  readonly obliquity: number;
  /** Zonal harmonic coefficients (unnormalised). */
  readonly J2: number;
  readonly J3: number;
  readonly J4: number;
  /** Radius of the sphere of influence with respect to its primary, m. */
  readonly sphereOfInfluence: number;
  /** Altitude above which the atmosphere is negligible for drag, m. */
  readonly atmosphereHeight: number;
  /** Mean surface gravity, m/s^2. */
  readonly surfaceGravity: number;
  /** Albedo, dimensionless. Used for lighting and thermal estimates. */
  readonly albedo: number;
}

export const EARTH: CelestialBodyParams = {
  id: 'earth',
  name: 'Earth',
  mu: 3.986004418e14,
  equatorialRadius: 6378137.0,
  polarRadius: 6356752.314245,
  flattening: 1 / 298.257223563,
  // Earth's mean sidereal rotation rate. Note this is 2*pi / 86164.0905, not
  // 2*pi / 86400 — using the solar day here is a classic source of a ~0.3%
  // ground-track drift error.
  rotationRate: 7.292115e-5,
  rotationPeriod: 86164.0905,
  // Mean obliquity of the ecliptic at J2000.0, 23.4392911 degrees. Written as a
  // conversion rather than a decimal literal so the value is exact to the digit
  // the IAU publishes and needs no rounding at the 17th place.
  obliquity: (23.4392911 * Math.PI) / 180,
  J2: 1.08262668e-3,
  J3: -2.53265648e-6,
  J4: -1.61962159e-6,
  sphereOfInfluence: 9.24e8,
  atmosphereHeight: 1_000_000,
  surfaceGravity: 9.7976,
  albedo: 0.306,
};

export const MOON: CelestialBodyParams = {
  id: 'moon',
  name: 'Moon',
  mu: 4.9028001e12,
  equatorialRadius: 1737400,
  polarRadius: 1735970,
  flattening: 0.0012,
  rotationRate: 2.6617e-6,
  rotationPeriod: 2360591.5,
  obliquity: 0.02692,
  J2: 2.033e-4,
  J3: 8.476e-6,
  J4: -9.591e-6,
  sphereOfInfluence: 6.61e7,
  atmosphereHeight: 0,
  surfaceGravity: 1.625,
  albedo: 0.12,
};

export const SUN: CelestialBodyParams = {
  id: 'sun',
  name: 'Sun',
  mu: 1.32712440018e20,
  equatorialRadius: 6.957e8,
  polarRadius: 6.957e8,
  flattening: 0,
  rotationRate: 2.865e-6,
  rotationPeriod: 2192832,
  obliquity: 0.1265,
  J2: 2.2e-7,
  J3: 0,
  J4: 0,
  sphereOfInfluence: Number.POSITIVE_INFINITY,
  atmosphereHeight: 0,
  surfaceGravity: 274,
  albedo: 0,
};

export const MARS: CelestialBodyParams = {
  id: 'mars',
  name: 'Mars',
  mu: 4.282837e13,
  equatorialRadius: 3396200,
  polarRadius: 3376200,
  flattening: 0.00589,
  rotationRate: 7.088218e-5,
  rotationPeriod: 88642.66,
  obliquity: 0.43965,
  J2: 1.96045e-3,
  J3: 3.145e-5,
  J4: -1.538e-5,
  sphereOfInfluence: 5.77e8,
  atmosphereHeight: 250_000,
  surfaceGravity: 3.7211,
  albedo: 0.25,
};

export const VENUS: CelestialBodyParams = {
  id: 'venus',
  name: 'Venus',
  mu: 3.24858592e14,
  equatorialRadius: 6051800,
  polarRadius: 6051800,
  flattening: 0,
  rotationRate: -2.9924e-7, // retrograde
  rotationPeriod: 20996798,
  obliquity: 3.0955,
  J2: 4.458e-6,
  J3: 0,
  J4: 0,
  sphereOfInfluence: 6.16e8,
  atmosphereHeight: 300_000,
  surfaceGravity: 8.87,
  albedo: 0.77,
};

export const JUPITER: CelestialBodyParams = {
  id: 'jupiter',
  name: 'Jupiter',
  mu: 1.26686534e17,
  equatorialRadius: 71492000,
  polarRadius: 66854000,
  flattening: 0.06487,
  rotationRate: 1.7585e-4,
  rotationPeriod: 35730,
  obliquity: 0.0546,
  J2: 1.4736e-2,
  J3: 0,
  J4: -5.87e-4,
  sphereOfInfluence: 4.82e10,
  atmosphereHeight: 1_000_000,
  surfaceGravity: 24.79,
  albedo: 0.503,
};

export const BODIES = { earth: EARTH, moon: MOON, sun: SUN, mars: MARS, venus: VENUS, jupiter: JUPITER } as const;

export type BodyId = keyof typeof BODIES;

export const getBody = (id: BodyId | string): CelestialBodyParams => {
  const body = (BODIES as Record<string, CelestialBodyParams | undefined>)[id];
  if (!body) throw new Error(`Unknown celestial body: ${id}`);
  return body;
};

// --- Earth-specific derived values used throughout the package ---------------

/** Mean lunar distance (semi-major axis of the Moon's orbit), m. */
export const MOON_ORBIT_RADIUS = 3.84400e8;

/** Sidereal month, s. */
export const MOON_ORBIT_PERIOD = 2360591.5;

/** Kármán line — the conventional boundary of space, m. */
export const KARMAN_LINE = 100_000;

/** Geostationary orbital radius, m. Derived so it stays consistent with mu. */
export const GEO_RADIUS = Math.cbrt(EARTH.mu / (EARTH.rotationRate * EARTH.rotationRate));

/** Geostationary altitude above the equator, m (≈ 35 786 km). */
export const GEO_ALTITUDE = GEO_RADIUS - EARTH.equatorialRadius;

/** Specific gas constant for dry air, J kg^-1 K^-1. */
export const R_AIR = 287.05287;

/** Ratio of specific heats for air, dimensionless. */
export const GAMMA_AIR = 1.4;

/** Universal gas constant, J mol^-1 K^-1. */
export const R_UNIVERSAL = 8.31446261815324;

/** Mean molar mass of air at sea level, kg/mol. */
export const M_AIR = 0.0289644;
